/**
 * Out-of-stock orders table with restore/cancel actions.
 *
 * Owns its own mutations and cancel-notes dialog state.
 */
import { Clock, EllipsisVertical, RotateCcw, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { OrderWithRelations } from '@snootsvet/shared'
import { formatName, matchesPredicate, ORDER_PREDICATES } from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import {
  AppointmentGroupCell,
  computeAppointmentRowSpans,
} from '@/frontend/features/orders/appointment-group'
import { NotesDialog } from '@/frontend/features/orders/checkout-screen/checkout-dialogs'
import { useUpdateOrder } from '@/frontend/features/orders/hooks/use-update-order'
import { OrderItemCell } from '@/frontend/features/orders/order-item-cell'

export interface CheckoutOutOfStockSectionProps {
  orders: OrderWithRelations[]
}

export function CheckoutOutOfStockSection({ orders }: CheckoutOutOfStockSectionProps) {
  const { mutate: updateOrder } = useUpdateOrder()
  const rowSpans = useMemo(() => computeAppointmentRowSpans(orders), [orders])

  const [cancelTarget, setCancelTarget] = useState<OrderWithRelations | null>(null)

  if (orders.length === 0) return null

  return (
    <div className="rounded-lg border border-border p-3">
      <h3 className="mb-2 flex items-center gap-2 text-headline-sm text-muted-foreground">
        <Clock className="h-4 w-4" />
        Out of Stock
      </h3>
      <Table size="sm" variant="borderless" className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead className="w-6" />
            <TableHead className="w-40">Item</TableHead>
            <TableHead className="w-14 text-center">Qty</TableHead>
            <TableHead className="w-28">Prescriber</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order, idx) => (
            <TableRow key={order.id} className="opacity-60">
              <TableCell />
              {rowSpans[idx] > 0 && <AppointmentGroupCell order={order} rowSpan={rowSpans[idx]} />}
              <OrderItemCell order={order} />
              <TableCell className="text-center">{order.quantity}</TableCell>
              <TableCell
                className="truncate text-muted-foreground"
                title={order.treatment?.staff ? formatName(order.treatment.staff) : undefined}
              >
                {order.treatment?.staff ? formatName(order.treatment.staff) : '—'}
              </TableCell>
              <TableCell className="text-muted-foreground">{order.notes ?? '—'}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={<Button size="icon" variant="ghost" aria-label="Row actions" />}
                  >
                    <EllipsisVertical className="h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" size="sm">
                    <DropdownMenuItem
                      onClick={() => updateOrder({ id: order.id, action: 'restore' })}
                    >
                      <RotateCcw className="h-4 w-4" />
                      Restore
                    </DropdownMenuItem>
                    {matchesPredicate(order, ORDER_PREDICATES.cancellable) && (
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => setCancelTarget(order)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Cancel
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <NotesDialog
        open={cancelTarget !== null}
        type="cancel"
        productName={cancelTarget?.product.name ?? ''}
        defaultValue={cancelTarget?.notes ?? ''}
        required
        onConfirm={(notes) => {
          if (!cancelTarget) return
          updateOrder({ id: cancelTarget.id, action: 'cancel', notes: notes || undefined })
          setCancelTarget(null)
        }}
        onCancel={() => setCancelTarget(null)}
      />
    </div>
  )
}
